// SchemaArgsForm — a form generated from a tool's input schema, prefilled with
// the proposed arguments (standing-agent-ui "Per-proposal decisions"; design
// D10). Kendo inputs per JSON type; objects/arrays (and unknown keys) edit as
// JSON text. Validation messages are rendered inline and tied to their input.

import { useState } from 'react';
import { Checkbox, Input, NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import type { SchemaField } from './schemaArgs';

export interface SchemaArgsFormProps {
  /** Distinguishes ids when several forms are on one page. */
  idPrefix: string;
  fields: SchemaField[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  errors: Record<string, string>;
  /** JSON text that doesn't parse, per field (reported to the parent). */
  onJsonError: (name: string, message: string | null) => void;
}

export function SchemaArgsForm({ idPrefix, fields, value, onChange, errors, onJsonError }: SchemaArgsFormProps) {
  const [jsonText, setJsonText] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.filter((f) => f.kind === 'json').map((f) => [f.name, value[f.name] === undefined ? '' : JSON.stringify(value[f.name], null, 2)]),
    ),
  );
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({});

  const set = (name: string, v: unknown) => onChange({ ...value, [name]: v });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {fields.map((f) => {
        const id = `${idPrefix}-${f.name}`;
        const errId = `${id}-error`;
        const error = errors[f.name] ?? jsonErrors[f.name];
        const described = [f.schema.description ? `${id}-desc` : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
        const common = { id, 'aria-invalid': error ? true : undefined, 'aria-describedby': described } as const;
        return (
          <div key={f.name}>
            <label htmlFor={id} className="pz-field-label">
              <span className="pz-mono">{f.name}</span>
              {f.label !== f.name && <span className="pz-muted"> — {f.label}</span>}
              {f.required && <span className="pz-muted"> (required)</span>}
            </label>
            {f.kind === 'string' && (
              <Input {...common} value={String(value[f.name] ?? '')} onChange={(e) => set(f.name, String(e.value ?? ''))} style={{ fontFamily: 'var(--font-mono)' }} />
            )}
            {f.kind === 'enum' && (
              <DropDownList
                {...common}
                data={f.schema.enum!.map((e) => String(e))}
                value={value[f.name] == null ? null : String(value[f.name])}
                onChange={(e) => {
                  const picked = f.schema.enum!.find((x) => String(x) === e.value);
                  set(f.name, picked ?? e.value);
                }}
                ariaLabel={f.name}
              />
            )}
            {(f.kind === 'number' || f.kind === 'integer') && (
              <NumericTextBox
                {...common}
                value={typeof value[f.name] === 'number' ? (value[f.name] as number) : null}
                format={f.kind === 'integer' ? 'n0' : undefined}
                spinners={false}
                onChange={(e) => set(f.name, e.value == null ? undefined : e.value)}
                ariaLabel={f.name}
                inputStyle={{ fontFamily: 'var(--font-mono)' }}
              />
            )}
            {f.kind === 'boolean' && (
              <Checkbox id={id} checked={value[f.name] === true} onChange={(e) => set(f.name, Boolean(e.value))} label={value[f.name] === true ? 'Yes' : 'No'} />
            )}
            {f.kind === 'json' && (
              <TextArea
                {...common}
                rows={4}
                value={jsonText[f.name] ?? ''}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}
                onChange={(e) => {
                  const text = String(e.value ?? '');
                  setJsonText((t) => ({ ...t, [f.name]: text }));
                  if (!text.trim()) {
                    setJsonErrors((x) => ({ ...x, [f.name]: '' }));
                    onJsonError(f.name, null);
                    set(f.name, undefined);
                    return;
                  }
                  try {
                    const parsed: unknown = JSON.parse(text);
                    setJsonErrors((x) => {
                      const next = { ...x };
                      delete next[f.name];
                      return next;
                    });
                    onJsonError(f.name, null);
                    set(f.name, parsed);
                  } catch {
                    const msg = `${f.name} isn't valid JSON.`;
                    setJsonErrors((x) => ({ ...x, [f.name]: msg }));
                    onJsonError(f.name, msg);
                  }
                }}
              />
            )}
            {f.schema.description && (
              <p id={`${id}-desc`} className="pz-hint">
                {f.schema.description}
              </p>
            )}
            {error && (
              <p id={errId} className="pz-error">
                {error}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default SchemaArgsForm;
